import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Mail, PenSquare, RefreshCw, Search, Users, Unplug, Loader2, Inbox, Send, Star } from 'lucide-react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ThreadList } from '../components/gmail/ThreadList';
import { ThreadView } from '../components/gmail/ThreadView';
import { ComposeDialog } from '../components/gmail/ComposeDialog';
import { AccessDialog } from '../components/gmail/AccessDialog';
import { GoogleCodesForm } from '../components/gmail/GoogleCodesForm';

const API = process.env.REACT_APP_BACKEND_URL;
const BOXES = [['inbox', 'Réception', Inbox], ['unread', 'Non lus', Mail], ['starred', 'Suivis', Star], ['sent', 'Envoyés', Send]];
const EMPTY = { to: '', cc: '', subject: '', body: '' };

function ConnectCard({ status, onSaved }) {
  const connect = async () => {
    try {
      const r = await axios.get(`${API}/api/gmail/auth-url`, { params: { origin: window.location.origin } });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Connexion impossible'); }
  };
  return (
    <div className="mx-auto mt-16 max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="gmail-connect-card">
      <Mail className="mx-auto h-10 w-10 text-[#1B3170]" />
      <h2 className="mt-3 text-lg font-bold text-slate-900">Boîte mail de l'entreprise</h2>
      {status.is_admin ? (
        <>
          <p className="mt-2 text-sm text-slate-500">Connectez la boîte Gmail de SESAM pour lire, répondre et envoyer vos e-mails depuis le CRM.</p>
          {!status.configured ? <GoogleCodesForm onSaved={onSaved} /> : (
            <p className="mt-4 text-xs text-emerald-700" data-testid="gmail-codes-ok">Codes Google enregistrés</p>
          )}
          <Button onClick={connect} disabled={!status.configured} className="mt-5 bg-[#1B3170] hover:bg-[#13245a]" data-testid="gmail-connect-btn">{status.configured ? 'Connecter Gmail' : '2. Connecter Gmail'}</Button>
          <div className="mt-6 space-y-2 rounded-lg bg-slate-50 p-3 text-left text-xs text-slate-600" data-testid="gmail-uris">
            <p>À déclarer dans Google Cloud (Clients → Application Web) :</p>
            <p><b>Origine JavaScript :</b><br /><code className="select-all break-all">{window.location.origin}</code></p>
            <p><b>URI de redirection :</b><br /><code className="select-all break-all" data-testid="gmail-redirect-uri">{`${window.location.origin}/api/oauth/gmail/callback`}</code></p>
          </div>
        </>
      ) : (
        <p className="mt-2 text-sm text-slate-500">{status.connected ? "Vous n'avez pas encore accès à la boîte mail. Demandez à l'administrateur." : "La boîte mail n'est pas encore connectée par l'administrateur."}</p>
      )}
    </div>
  );
}

export default function BoiteMail({ user, onLogout }) {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [box, setBox] = useState('inbox');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ threads: [], next_page_token: null });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [compose, setCompose] = useState(null);
  const [showAccess, setShowAccess] = useState(false);
  const [error, setError] = useState('');

  const loadStatus = () => axios.get(`${API}/api/gmail/status`).then((r) => setStatus(r.data));
  useEffect(() => {
    loadStatus();
    const g = params.get('gmail');
    if (g) {
      ({ ok: () => toast.success('Gmail connecté'), refuse: () => toast.error('Connexion refusée'), erreur: () => toast.error('Échec de la connexion Gmail'), permissions: () => toast.error('Cochez toutes les autorisations Google, puis reconnectez Gmail', { duration: 10000 }) }[g] || (() => {}))();
      setParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async (pageToken) => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/api/gmail/threads`, { params: { box, q, page_token: pageToken } });
      setData((d) => (pageToken ? { threads: [...d.threads, ...r.data.threads], next_page_token: r.data.next_page_token } : r.data));
    } catch (e) { const m = e?.response?.data?.detail || 'Erreur de chargement'; setError(typeof m === 'string' ? m : 'Erreur de chargement'); setData({ threads: [], next_page_token: null }); } finally { setLoading(false); }
  }, [box, q]);

  useEffect(() => { if (status?.can_access) load(); }, [status, box]); // eslint-disable-line react-hooks/exhaustive-deps

  const select = (id) => { setSelected(id); setData((d) => ({ ...d, threads: d.threads.map((t) => (t.id === id ? { ...t, unread: false } : t)) })); };
  const star = async (t) => {
    await axios.post(`${API}/api/gmail/threads/${t.id}/star`, null, { params: { starred: !t.starred } });
    setData((d) => ({ ...d, threads: d.threads.map((x) => (x.id === t.id ? { ...x, starred: !t.starred } : x)) }));
  };
  const reply = (thread, m) => {
    const quoted = `\n\n\nLe ${new Date(m.date).toLocaleString('fr-FR')}, ${m.from_name} a écrit :\n> ${(m.text || '').split('\n').join('\n> ')}`;
    const to = m.from_email === status.email ? m.to : m.from_email;
    setCompose({ to, cc: '', subject: m.subject.startsWith('Re:') ? m.subject : `Re: ${m.subject}`, body: quoted, thread_id: thread.id, in_reply_to: m.message_id, references: m.references });
  };
  const disconnect = async () => {
    if (!window.confirm('Déconnecter la boîte Gmail du CRM ?')) return;
    await axios.post(`${API}/api/gmail/disconnect`);
    setSelected(null); loadStatus();
  };

  return (
    <Layout user={user} onLogout={onLogout} currentPage="boite-mail">
      <div className="p-6 lg:p-8" data-testid="gmail-page">
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Boîte mail</h1>
            {status?.email && <p className="text-sm text-slate-500" data-testid="gmail-connected-email">{status.email}</p>}
          </div>
          {status?.can_access && (
            <div className="ml-auto flex flex-wrap gap-2">
              <Button onClick={() => setCompose(EMPTY)} className="gap-2 bg-[#1B3170] hover:bg-[#13245a]" data-testid="gmail-new-btn"><PenSquare className="h-4 w-4" />Nouveau message</Button>
              {status.is_admin && <Button variant="outline" onClick={() => setShowAccess(true)} className="gap-2" data-testid="gmail-access-btn"><Users className="h-4 w-4" />Accès</Button>}
              {status.is_admin && <Button variant="outline" onClick={disconnect} className="gap-2 text-red-600" data-testid="gmail-disconnect-btn"><Unplug className="h-4 w-4" />Déconnecter</Button>}
            </div>
          )}
        </div>

        {!status ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /> : !status.can_access ? <ConnectCard status={status} onSaved={loadStatus} /> : (
          <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <div className="flex gap-1 border-b border-slate-100 p-2">
                {BOXES.map(([id, label, Icon]) => (
                  <button key={id} onClick={() => { setBox(id); setSelected(null); }} data-testid={`gmail-box-${id}`}
                    className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${box === id ? 'bg-[#1B3170] text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                    <Icon className="h-3.5 w-3.5" />{label}
                  </button>
                ))}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 border-b border-slate-100 p-2">
                <div className="relative flex-1"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-8 h-9" data-testid="gmail-search-input" /></div>
                <Button type="button" variant="ghost" size="icon" onClick={() => load()} data-testid="gmail-refresh-btn"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
              </form>
              <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                {error && <p className="m-3 rounded-lg bg-red-50 p-3 text-sm text-red-700" data-testid="gmail-error">{error}</p>}
                {loading && !data.threads.length ? <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-slate-400" /> : !error && <ThreadList threads={data.threads} selectedId={selected} onSelect={select} onStar={star} />}
                {data.next_page_token && <Button variant="ghost" className="w-full" onClick={() => load(data.next_page_token)} disabled={loading} data-testid="gmail-load-more">Afficher plus</Button>}
              </div>
            </div>
            <div className="min-h-[400px] overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50/60 max-h-[calc(100vh-200px)]">
              {selected ? <ThreadView threadId={selected} onReply={reply} /> : <p className="p-10 text-center text-sm text-slate-500">Sélectionnez un e-mail pour le lire</p>}
            </div>
          </div>
        )}
      </div>
      {compose && <ComposeDialog open={!!compose} onOpenChange={(o) => !o && setCompose(null)} initial={compose} onSent={() => load()} />}
      {status?.is_admin && status.connected && <AccessDialog open={showAccess} onOpenChange={setShowAccess} status={status} onSaved={loadStatus} />}
    </Layout>
  );
}
